<template>
  <div>
    <button
      type="button"
      class="btn btn-secondary"
      @click="showRequestModal"
    >
      Submit Holiday Work
    </button>

    <div
      id="holidayWorkRequestModal"
      class="modal fade"
      tabindex="-1"
      aria-labelledby="holidayWorkRequestModalLabel"
      aria-hidden="true"
    >
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h1
              id="holidayWorkRequestModalLabel"
              class="modal-title fs-5"
            >
              Working During Public Holiday
            </h1>
            <button
              type="button"
              class="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
            />
          </div>
          <ValidationObserver v-slot="{ invalid }">
            <form
              ref="holidayWorkForm"
              @submit.prevent="submitHolidayWork"
            >
              <div class="modal-body">
                <div class="mb-3">
                  <label
                    for="holidayName"
                    class="form-label"
                  >
                    Holiday Name
                  </label>
                  <ValidationProvider
                    v-slot="v"
                    name="Holiday name"
                    rules="required"
                  >
                    <input
                      id="holidayName"
                      v-model="holidayName"
                      type="text"
                      name="holiday_name"
                      class="form-control"
                      required
                    >
                    <span class="text-danger">{{ v.errors[0] }}</span>
                  </ValidationProvider>
                </div>

                <div class="mb-3">
                  <label
                    for="holidayDateRange"
                    class="form-label"
                  >
                    Date Range
                  </label>
                  <vc-date-picker
                    v-model="dateRange"
                    is-range
                    :masks="masks"
                  >
                    <template #default="{ inputValue, inputEvents }">
                      <div class="d-flex align-items-center gap-2 flex-wrap">
                        <input
                          :value="inputValue.start"
                          class="form-control form-control-sm"
                          style="max-width: 140px"
                          name="start_date"
                          v-on="inputEvents.start"
                        >
                        <span class="text-muted">–</span>
                        <input
                          :value="inputValue.end"
                          class="form-control form-control-sm"
                          style="max-width: 140px"
                          name="end_date"
                          v-on="inputEvents.end"
                        >
                        <div
                          v-if="submitting"
                          class="spinner-grow spinner-grow-sm text-primary"
                          role="status"
                        >
                          <span class="visually-hidden">Loading...</span>
                        </div>
                      </div>
                    </template>
                  </vc-date-picker>
                </div>

                 <div class="mb-3">
                   <label
                     for="holidayWorkReason"
                     class="form-label"
                   >
                     Reason
                   </label>
                   <ValidationProvider
                     v-slot="v"
                     name="Reason"
                     rules="required"
                   >
                     <textarea
                       id="holidayWorkReason"
                       v-model="reason"
                       name="reason"
                       class="form-control"
                       rows="3"
                       required
                     />
                     <span class="text-danger">{{ v.errors[0] }}</span>
                   </ValidationProvider>
                 </div>
                 <div
                   v-if="dateCount === 1"
                   class="mb-3"
                 >
                   <div class="form-check">
                     <input
                       id="halfDayCheck"
                       v-model="halfDay"
                       type="checkbox"
                       name="half_day"
                       class="form-check-input"
                     >
                     <label
                       for="halfDayCheck"
                       class="form-check-label"
                     >Half Day Off</label>
                   </div>
                 </div>
              </div>
              <div class="modal-footer">
                <button
                  type="button"
                  class="btn btn-secondary"
                  data-bs-dismiss="modal"
                >
                  Close
                </button>
                <button
                  type="submit"
                  class="btn btn-primary"
                  :disabled="invalid || !hasValidDateRange || submitting"
                >
                  Submit
                </button>
              </div>
            </form>
          </ValidationObserver>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Modal } from "bootstrap";
import moment from "moment-timezone";
import { extend, ValidationObserver, ValidationProvider } from "vee-validate";
import { required } from "vee-validate/dist/rules";
import { mapActions } from "vuex";

extend("required", required);

export default {
    name: "StaffHolidayWorkRequestButton",
    components: {
        ValidationObserver,
        ValidationProvider,
    },
    props: {
        staffId: {
            type: Number,
            required: true,
        },
        year: {
            type: Number,
            required: false,
            default: () => moment().year(),
        },
    },
    data() {
        return {
            submitting: false,
            holidayName: null,
            dateRange: {
                start: moment().format("YYYY-MM-DD"),
                end: moment().format("YYYY-MM-DD"),
            },
            reason: null,
            halfDay: false,
            masks: {
                input: "YYYY-MM-DD",
            },
        };
    },
    computed: {
        requestModal() {
            return Modal.getOrCreateInstance(document.getElementById("holidayWorkRequestModal"));
        },
        hasValidDateRange() {
            return this.dateRange?.start && this.dateRange?.end;
        },
        dateCount() {
            if (!this.dateRange || !this.dateRange.start || !this.dateRange.end) {
                return 0;
            }
            const start = moment(this.dateRange.start).startOf("date");
            const end = moment(this.dateRange.end).startOf("date");
            return end.diff(start, "days") + 1;
        },
    },
    methods: {
        async submitHolidayWork() {
            this.submitting = true;

            try {
                const formData = new FormData(this.$refs.holidayWorkForm);
                formData.append("staff_id", this.staffId.toString());
                formData.append(
                    "start_date",
                    this.dateRange?.start ? moment(this.dateRange.start).format("YYYY-MM-DD") : ""
                );
                formData.append(
                    "end_date",
                    this.dateRange?.end ? moment(this.dateRange.end).format("YYYY-MM-DD") : ""
                );
                formData.append("holiday_name", this.holidayName ?? "");
                formData.append("reason", this.reason ?? "");
                formData.append("half_day", this.halfDay ? "1" : "0");

                await this.sendHolidayWorkRequest({ formData, staffId: this.staffId });
                await this.getHolidayWorkRequests({ staffId: this.staffId, year: this.year });

                this.resetForm();
                this.requestModal.hide();
                this.$emit("update-table");

                alert("Holiday work request submitted successfully");
            } catch (e) {
                let errorMessage = "Something went wrong, please try again later";
                if (e.response && e.response.data && e.response.data.message) {
                    errorMessage = e.response.data.message;
                } else if (e.response && e.response.data && e.response.data.errors) {
                    errorMessage = Object.values(e.response.data.errors).flat().join("\n");
                }
                alert(errorMessage);
                console.error(e);
            } finally {
                this.submitting = false;
            }
        },
        resetForm() {
            this.holidayName = null;
            this.dateRange = {
                start: moment().format("YYYY-MM-DD"),
                end: moment().format("YYYY-MM-DD"),
            };
            this.reason = null;
            this.halfDay = false;
            this.$refs.holidayWorkForm?.reset();
        },
        showRequestModal() {
            this.resetForm();
            this.requestModal.show();
        },
        ...mapActions({
            sendHolidayWorkRequest: "vacation/sendHolidayWorkRequest",
            getHolidayWorkRequests: "vacation/getHolidayWorkRequests",
        }),
    },
};
</script>
